import { t } from "@travily/i18n";
import { ChevronDownIcon } from "@ui/components/Icon/ChevronDownIcon";
import { formatPriceWhole } from "@ui/lib/format-price";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { useState } from "react";
import { type Costs, PUBLISHED_CURRENCY } from "../../apply-household";
import { LineItemList } from "./LineItemList";
import { categoryLabel } from "./labels";
import { ProvenanceBadge } from "./ProvenanceBadge";

type Category = Costs["categories"][number];

const categoryBreakdown = tv({
  slots: {
    root: "flex flex-col",
    row: "border-neutral-100 border-b py-3 last:border-b-0",
    header:
      "flex w-full cursor-pointer items-center gap-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2",
    label: "font-medium text-base text-neutral-900",
    // The share, as a plain bar: seven of these say where the money goes
    // faster than seven percentages do.
    track: "mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-neutral-100",
    fill: "h-full rounded-full bg-red-300",
    amounts: "flex shrink-0 flex-col items-end",
    amount: "text-base text-neutral-900 tabular-nums",
    share: "text-neutral-400 text-xs tabular-nums",
    chevron: "size-4 shrink-0 text-neutral-400 transition-transform",
  },
  variants: {
    expanded: { true: { chevron: "rotate-180" } },
  },
});

export interface CategoryBreakdownProps {
  categories: Category[];
}

export function CategoryBreakdown({ categories }: CategoryBreakdownProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const styles = categoryBreakdown();

  return (
    <div className={styles.root()}>
      {categories.map((category) => {
        const isExpanded = expanded === category.key;
        const share = category.share ?? 0;
        const hasItems = category.items.length > 0;

        return (
          <div className={styles.row()} key={category.key}>
            <Button
              aria-expanded={hasItems ? isExpanded : undefined}
              className={styles.header()}
              isDisabled={!hasItems}
              onPress={() => setExpanded(isExpanded ? null : category.key)}
            >
              <span className="min-w-0 flex-1">
                <span className={styles.label()}>{categoryLabel(category.key)}</span>
                <span aria-hidden className={styles.track()}>
                  <span className={styles.fill()} style={{ width: `${Math.round(share * 100)}%` }} />
                </span>
              </span>
              <span className={styles.amounts()}>
                <span className={styles.amount()}>
                  {category.p50_usd_minor === null ? "—" : formatPriceWhole(category.p50_usd_minor, PUBLISHED_CURRENCY)}
                </span>
                <span className={styles.share()}>
                  {t("components.views.destinations.costs.category_share", { share: Math.round(share * 100) })}
                </span>
              </span>
              <ProvenanceBadge className="hidden sm:inline-flex" grade={category.grade} />
              {hasItems && <ChevronDownIcon className={styles.chevron({ expanded: isExpanded })} />}
            </Button>
            {hasItems && isExpanded && <LineItemList items={category.items} />}
          </div>
        );
      })}
    </div>
  );
}
